import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CinemaRechargeModal from '../components/RechargeModal';
import {
  createMemberPaymentTicket,
  createMemberRechargeOrder,
  fetchHomeDisplayConfig,
  fetchMemberLibrary,
  fetchMemberPaymentOptions,
  fetchMemberPointLogs,
  fetchMemberRechargeOrders,
  logoutMember,
  queryMemberRechargeOrder,
  updateMemberPassword,
  updateMemberProfile,
  type MemberPointLog,
  type MemberRechargeOrder,
} from '@/api/member';
import {
  buildSessionFromProfile,
  clearMemberSession,
  ensureMemberAccessToken,
  readMemberSession,
  refreshCurrentMember,
  saveMemberSession,
  type MemberSession,
} from '@/utils/memberSession';
import { destroyCustomerService, openCustomerService } from '@/utils/customerService';
import { isAndroidNativeAppRuntime, isNativeAppRuntime } from '@/utils/nativeApp';

const fallbackAppDownloadUrl = 'https://img.mfantasy.net/app/vod-0824a.apk';

interface ProfileMenuItem {
  id: string;
  icon: string;
  labelKey: string;
  color: string;
  bg: string;
  dealerOnly?: boolean;
}

const menuGroups: Array<{ titleKey: string; items: ProfileMenuItem[] }> = [
  {
    titleKey: 'cinema_profileAccount',
    items: [
      { id: 'edit-profile', icon: 'ri-user-settings-line', labelKey: 'cinema_editProfile', color: 'text-rose-400', bg: 'bg-rose-500/15' },
      { id: 'change-password', icon: 'ri-lock-password-line', labelKey: 'cinema_changePassword', color: 'text-sky-400', bg: 'bg-sky-500/15' },
      { id: 'recharge', icon: 'ri-bank-card-line', labelKey: 'cinema_rechargeTitle', color: 'text-accent-400', bg: 'bg-accent-500/15' },
      { id: 'referral', icon: 'ri-share-forward-box-line', labelKey: 'cinema_referral', color: 'text-yellow-400', bg: 'bg-yellow-500/15' },
      { id: 'orders', icon: 'ri-receipt-line', labelKey: 'cinema_rechargeOrders', color: 'text-orange-400', bg: 'bg-orange-500/15' },
      { id: 'points', icon: 'ri-coin-line', labelKey: 'cinema_coinLedger', color: 'text-amber-400', bg: 'bg-amber-500/15' },
      { id: 'commissions', icon: 'ri-money-cny-circle-line', labelKey: 'cinema_commissionRecords', color: 'text-emerald-400', bg: 'bg-emerald-500/15', dealerOnly: true },
      { id: 'purchases', icon: 'ri-shopping-bag-line', labelKey: 'cinema_myPurchases', color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
      { id: 'creator-center', icon: 'ri-movie-2-line', labelKey: 'cinema_creatorCenter', color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/15' },
    ],
  },
  {
    titleKey: 'cinema_profileContent',
    items: [
      { id: 'history', icon: 'ri-time-line', labelKey: 'cinema_watchHistory', color: 'text-violet-400', bg: 'bg-violet-500/15' },
      { id: 'playback', icon: 'ri-play-circle-line', labelKey: 'cinema_playbackSettings', color: 'text-teal-400', bg: 'bg-teal-500/15' },
      { id: 'contact', icon: 'ri-customer-service-2-line', labelKey: 'cinema_about', color: 'text-cyan-400', bg: 'bg-cyan-500/15' },
    ],
  },
];

export default function CinemaProfilePage() {
  const { t } = useTranslation('cinema');
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [member, setMember] = useState<MemberSession>(() => readMemberSession());
  const [collectionCount, setCollectionCount] = useState(0);
  const [historyCount, setHistoryCount] = useState(0);
  const [activeSheet, setActiveSheet] = useState<'account' | 'password' | 'playback' | 'orders' | 'points' | null>(null);
  const [orders, setOrders] = useState<MemberRechargeOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [pointLogs, setPointLogs] = useState<MemberPointLog[]>([]);
  const [pointLogsLoading, setPointLogsLoading] = useState(false);
  const [nickName, setNickName] = useState(() => readMemberSession().name);
  const [email, setEmail] = useState(() => readMemberSession().email);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [autoPlayNext, setAutoPlayNext] = useState(() => window.localStorage.getItem('mfantasy_auto_play_next') !== 'false');
  const [appDownloadUrl, setAppDownloadUrl] = useState('');
  const [creatorCenterEnabled, setCreatorCenterEnabled] = useState(false);
  const [customerServiceConfig, setCustomerServiceConfig] = useState({
    enabled: true,
    baseUrl: 'https://mechat.click',
    tenantId: 'tenant-demo',
    appId: 'default',
    webChannelId: 'web',
    appChannelId: 'app',
  });
  const [contactError, setContactError] = useState('');
  const hideAppDownload = isAndroidNativeAppRuntime();
  const effectiveAppDownloadUrl = appDownloadUrl || fallbackAppDownloadUrl;

  useEffect(() => {
    if (!member.isLoggedIn) {
      navigate('/login');
      return;
    }
    void refreshCurrentMember().then((next) => {
      setMember(next);
      setNickName(next.name);
      setEmail(next.email);
    }).catch(() => undefined);
    void ensureMemberAccessToken()
      .then((token) => token ? fetchMemberLibrary(token) : Promise.resolve({ recent: [], favorites: [] }))
      .then((library) => {
        setCollectionCount(library.favorites?.length || 0);
        setHistoryCount(library.recent?.length || 0);
      })
      .catch(() => undefined);
  }, [member.isLoggedIn, navigate]);

  useEffect(() => {
    void fetchHomeDisplayConfig()
      .then((config) => {
        if (!hideAppDownload) setAppDownloadUrl((config.appDownloadUrl || '').trim());
        setCreatorCenterEnabled(config.creatorCenterEnabled !== false);
        setCustomerServiceConfig({
          enabled: config.customerServiceEnabled !== false,
          baseUrl: (config.customerServiceBaseUrl || 'https://mechat.click').trim(),
          tenantId: (config.customerServiceTenantId || 'tenant-demo').trim(),
          appId: (config.customerServiceAppId || 'default').trim(),
          webChannelId: (config.customerServiceWebChannelId || 'web').trim(),
          appChannelId: (config.customerServiceAppChannelId || 'app').trim(),
        });
      })
      .catch(() => {
        if (!hideAppDownload) setAppDownloadUrl('');
      });
  }, [hideAppDownload]);

  useEffect(() => () => destroyCustomerService(), []);

  const openOrders = () => {
    setActiveSheet('orders');
    setOrdersLoading(true);
    void ensureMemberAccessToken()
      .then((token) => token ? fetchMemberRechargeOrders(token) : Promise.reject(new Error('unauthorized')))
      .then((result) => setOrders(result.list || []))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  };

  const openPointLogs = () => {
    setActiveSheet('points');
    setPointLogsLoading(true);
    void ensureMemberAccessToken()
      .then((token) => token ? fetchMemberPointLogs(token) : Promise.reject(new Error('unauthorized')))
      .then((result) => setPointLogs(result.list || []))
      .catch(() => setPointLogs([]))
      .finally(() => setPointLogsLoading(false));
  };

  const saveProfile = () => {
    const nextName = nickName.trim();
    if (!nextName || profileSaving) return;
    setProfileSaving(true);
    setProfileMessage('');
    void ensureMemberAccessToken()
      .then((token) => token ? updateMemberProfile(nextName, email.trim(), token) : Promise.reject(new Error('unauthorized')))
      .then((profile) => {
        const next = buildSessionFromProfile(profile, readMemberSession());
        saveMemberSession(next);
        setMember(next);
        setNickName(next.name);
        setEmail(next.email);
        setProfileMessage(t('cinema_profileSaved'));
      })
      .catch((error: unknown) => setProfileMessage(error instanceof Error ? error.message : t('cinema_profileSaveFailed')))
      .finally(() => setProfileSaving(false));
  };

  const savePassword = () => {
    if (passwordSaving) return;
    if (newPassword.length < 6) {
      setPasswordMessage(t('cinema_passwordHint'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage(t('cinema_passwordMismatch'));
      return;
    }
    setPasswordSaving(true);
    setPasswordMessage('');
    void ensureMemberAccessToken()
      .then((token) => token ? updateMemberPassword(newPassword, confirmPassword, token) : Promise.reject(new Error('unauthorized')))
      .then(() => {
        setNewPassword('');
        setConfirmPassword('');
        setPasswordMessage(t('cinema_passwordSaved'));
      })
      .catch((error: unknown) => setPasswordMessage(error instanceof Error ? error.message : t('cinema_passwordSaveFailed')))
      .finally(() => setPasswordSaving(false));
  };

  const handleMenuClick = (id: string) => {
    if (id === 'history') {
      navigate('/history');
    } else if (id === 'purchases') {
      navigate('/history?tab=purchases');
    } else if (id === 'points') {
      openPointLogs();
    } else if (id === 'recharge') {
      setRechargeOpen(true);
    } else if (id === 'orders') {
      openOrders();
    } else if (id === 'referral') {
      navigate('/referral');
    } else if (id === 'commissions') {
      navigate('/referral?tab=commissions');
    } else if (id === 'creator-center') {
      navigate('/creator-center');
    } else if (id === 'edit-profile') {
      setNickName(member.name);
      setEmail(member.email);
      setProfileMessage('');
      setActiveSheet('account');
    } else if (id === 'change-password') {
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage('');
      setActiveSheet('password');
    } else if (id === 'playback') {
      setActiveSheet('playback');
    } else if (id === 'contact') {
      setContactError('');
      if (!customerServiceConfig.enabled || !customerServiceConfig.baseUrl || !customerServiceConfig.tenantId) {
        setContactError(t('cinema_contactUnavailable'));
        return;
      }
      void openCustomerService({
        baseUrl: customerServiceConfig.baseUrl,
        tenantId: customerServiceConfig.tenantId,
        appId: customerServiceConfig.appId,
        channelId: isNativeAppRuntime() ? customerServiceConfig.appChannelId : customerServiceConfig.webChannelId,
        userId: String(member.id),
        userName: member.name,
        avatarUrl: member.avatar,
      }).catch(() => setContactError(t('cinema_contactUnavailable')));
    }
  };

  const handleLogout = async () => {
    const session = readMemberSession();
    try {
      if (session.accessToken && session.refreshToken) {
        await logoutMember(session.accessToken, session.refreshToken);
      }
    } catch {
      // Local logout must remain available while the API is unreachable.
    } finally {
      clearMemberSession();
      setShowLogoutConfirm(false);
      navigate('/');
    }
  };

  return (
    <div className="pt-14 md:pt-16 pb-20 md:pb-24">
      <div className="max-w-2xl mx-auto px-4 md:px-10 lg:px-14">
        {/* Page header */}
        <div className="flex items-center justify-between gap-4 py-4 md:py-6">
          <h1 className="text-lg md:text-2xl font-bold font-heading text-foreground-50">
            {t('cinema_profile')}
          </h1>
          {!hideAppDownload && (
            <a
              href={effectiveAppDownloadUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-accent-500/25 bg-accent-500/10 px-3 text-xs font-semibold text-accent-400 transition-colors hover:border-accent-500/45 hover:bg-accent-500/15 md:h-10 md:text-sm"
            >
              <i className="ri-download-2-line text-base" aria-hidden="true" />
              {t('cinema_appDownloadButton')}
            </a>
          )}
        </div>

        {contactError && <div className="fixed bottom-20 left-1/2 z-[70] -translate-x-1/2 rounded-lg border border-red-500/25 bg-background-900 px-4 py-2 text-sm text-red-400 shadow-xl">{contactError}</div>}

        {/* User card */}
        <div className="relative overflow-hidden rounded-2xl border border-background-800/60 p-5 md:p-6 mb-5 shadow-xl">
          <img
            src="/assets/readdy/profile-card-bg-02-9e54c73609.jpg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/45 to-background-900/20" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60" />

          <div className="relative flex items-center gap-4">
            <img
              src={member.avatar || '/assets/readdy/profile-cinema-01-e8b7d80192.jpg'}
              alt={member.name}
              className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-accent-500/30 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h2 className="text-base md:text-lg font-bold text-foreground-50 font-heading">
                {member.name}
              </h2>
              <p className="text-[11px] md:text-xs text-foreground-500 mt-0.5">
                ID: {member.id}
              </p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="flex items-center gap-1 text-xs text-foreground-400">
                  <span className="w-3.5 h-3.5 flex items-center justify-center text-accent-500 flex-shrink-0">
                    <i className="ri-coin-line text-xs"></i>
                  </span>
                  {t('cinema_coinAmount', { count: member.pointsBalance })}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold text-amber-900 bg-amber-400 whitespace-nowrap">
                {member.isDealer ? t('cinema_dealerLabel') : t('cinema_memberLabel')}
              </span>
              <button onClick={() => { setNickName(member.name); setEmail(member.email); setProfileMessage(''); setNewPassword(''); setConfirmPassword(''); setPasswordMessage(''); setActiveSheet('account'); }} className="px-4 py-2 rounded-full text-xs font-semibold text-[#1A1308] bg-[#F19744] hover:bg-[#F4A15A] transition-all cursor-pointer whitespace-nowrap">
                {t('cinema_editProfile')}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="relative grid grid-cols-4 gap-1.5 mt-5 pt-5 border-t border-background-800/50 md:gap-3">
            <div className="text-center">
              <p className="text-lg md:text-xl font-bold text-foreground-100">{historyCount}</p>
              <p className="text-[10px] md:text-xs text-foreground-500 mt-0.5">{t('cinema_watchHistory')}</p>
            </div>
            <div className="text-center">
              <p className="text-lg md:text-xl font-bold text-foreground-100">{collectionCount}</p>
              <p className="text-[10px] md:text-xs text-foreground-500 mt-0.5">{t('cinema_collectionCount')}</p>
            </div>
            <div className="text-center">
              <button onClick={openPointLogs} className="group cursor-pointer">
                <p className="text-lg md:text-xl font-bold text-accent-400 group-hover:text-accent-300">{member.pointsBalance}</p>
                <p className="text-[10px] md:text-xs text-foreground-500 mt-0.5">{t('cinema_myCoins')}</p>
              </button>
            </div>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setRechargeOpen(true)}
                className="group inline-flex flex-col items-center cursor-pointer"
                aria-label={t('cinema_rechargeTitle')}
              >
                <span className="relative flex h-8 min-w-[52px] items-center justify-center overflow-hidden rounded-lg border border-[#F19744]/70 bg-[#F19744] px-2 shadow-[0_4px_14px_rgba(241,151,68,0.24)] transition-all group-hover:-translate-y-0.5 group-hover:bg-[#F4A15A] md:h-9 md:min-w-[58px]">
                  <span className="relative text-xs font-black text-[#1A1308] md:text-sm">{t('cinema_rechargeTitle')}</span>
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Menu groups */}
        <div className="space-y-5">
          {menuGroups.map((group, gIdx) => (
            <div key={gIdx}>
              <h3 className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-2.5 px-1">
                {t(group.titleKey)}
              </h3>
              <div className="rounded-xl bg-background-900/60 border border-background-800/60 overflow-hidden">
                {group.items.filter((item) =>
                  (!item.dealerOnly || member.isDealer) &&
                  (item.id !== 'creator-center' || creatorCenterEnabled)
                ).map((item, iIdx, visibleItems) => (
                  <button
                    key={item.id}
                    onClick={() => handleMenuClick(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 md:py-3.5 hover:bg-background-800/40 transition-all cursor-pointer text-left ${
                      iIdx < visibleItems.length - 1 ? 'border-b border-background-800/40' : ''
                    }`}
                  >
                    <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${item.bg}`}>
                      <i className={`${item.icon} text-base ${item.color}`}></i>
                    </span>
                    <span className="flex-1 text-sm font-medium text-foreground-300">{t(item.labelKey)}</span>
                    {item.id === 'points' && (
                      <span className="text-sm font-semibold text-accent-400">
                        {member.pointsBalance}
                      </span>
                    )}
                    <span className="w-4 h-4 flex items-center justify-center text-foreground-600 flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-sm"></i>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Logout */}
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="w-full mt-6 py-3.5 rounded-xl text-sm font-medium text-red-400 bg-background-900/60 border border-background-800/60 hover:bg-red-500/10 hover:border-red-500/20 transition-all cursor-pointer whitespace-nowrap"
        >
          {t('cinema_logout')}
        </button>

        {/* Version */}
        <p className="text-center text-[10px] text-foreground-700 mt-4">
          {t('cinema_version', { version: '1.2.0' })}
        </p>
      </div>

      {/* Logout Confirm Modal */}
      {showLogoutConfirm && (
        <div className="cinema-modal-safe fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowLogoutConfirm(false)} />
          <div className="relative bg-background-900 border border-background-800 rounded-t-2xl sm:rounded-2xl p-5 md:p-6 w-full sm:max-w-xs shadow-2xl animate-ao-fade-in-up">
            <div className="text-center">
              <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-red-500/15">
                <i className="ri-logout-box-r-line text-xl text-red-400"></i>
              </span>
              <h3 className="text-base font-bold text-foreground-50 font-heading">{t('cinema_logoutConfirm')}</h3>
              <p className="text-xs text-foreground-500 mt-1.5">{t('cinema_logoutConfirmDesc')}</p>
            </div>
            <div className="flex gap-2.5 mt-5">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-foreground-400 bg-background-800 hover:bg-background-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('cinema_cancel')}
              </button>
              <button
				onClick={() => void handleLogout()}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-background-50 bg-red-500 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('cinema_logout')}
              </button>
            </div>
          </div>
        </div>
      )}
      {activeSheet && (
        <div className="cinema-modal-safe fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setActiveSheet(null)} />
          <div className="relative w-full max-h-[78vh] overflow-y-auto rounded-t-2xl border border-background-800 bg-background-900 p-4 shadow-2xl animate-ao-fade-in-up sm:max-w-md sm:rounded-2xl md:p-5">
            <button onClick={() => setActiveSheet(null)} className="absolute right-4 top-4 w-8 h-8 rounded-full bg-background-800 text-foreground-500 hover:text-foreground-200 cursor-pointer"><i className="ri-close-line" /></button>
            {activeSheet === 'account' && <div>
              <h3 className="pr-10 text-lg font-bold text-foreground-50 font-heading">{t('cinema_editProfile')}</h3>
              <div className="mt-3 space-y-2.5">
                <label className="block rounded-lg bg-background-800/60 px-3 py-2.5"><span className="text-[11px] text-foreground-500">{t('cinema_accountName')}</span><input value={nickName} onChange={(event) => setNickName(event.target.value)} maxLength={32} className="mt-1 w-full bg-transparent text-sm text-foreground-200 outline-none placeholder:text-foreground-600" placeholder={t('cinema_accountName')} /></label>
                <label className="block rounded-lg bg-background-800/60 px-3 py-2.5"><span className="text-[11px] text-foreground-500">{t('cinema_accountEmail')}</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} maxLength={100} className="mt-1 w-full bg-transparent text-sm text-foreground-200 outline-none placeholder:text-foreground-600" placeholder={t('cinema_emailPlaceholder')} /></label>
                {profileMessage && <p className={profileMessage === t('cinema_profileSaved') ? 'text-xs text-accent-400' : 'text-xs text-red-400'}>{profileMessage}</p>}
                <button onClick={saveProfile} disabled={!nickName.trim() || profileSaving} className="w-full rounded-lg bg-accent-500 px-3 py-2.5 text-sm font-semibold text-background-950 hover:bg-accent-500/90 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer">{profileSaving ? t('cinema_loading') : t('cinema_saveProfile')}</button>
              </div>
            </div>}
            {activeSheet === 'password' && <div>
              <h3 className="pr-10 text-lg font-bold text-foreground-50 font-heading">{t('cinema_changePassword')}</h3>
              <p className="mt-1 text-[11px] text-foreground-600">{t('cinema_passwordHint')}</p>
              <div className="mt-3 space-y-2.5">
                <label className="block rounded-lg bg-background-800/60 px-3 py-2.5"><span className="text-[11px] text-foreground-500">{t('cinema_newPassword')}</span><input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" className="mt-1 w-full bg-transparent text-sm text-foreground-200 outline-none placeholder:text-foreground-600" placeholder={t('cinema_newPassword')} /></label>
                <label className="block rounded-lg bg-background-800/60 px-3 py-2.5"><span className="text-[11px] text-foreground-500">{t('cinema_confirmPassword')}</span><input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" className="mt-1 w-full bg-transparent text-sm text-foreground-200 outline-none placeholder:text-foreground-600" placeholder={t('cinema_confirmPassword')} /></label>
                {passwordMessage && <p className={passwordMessage === t('cinema_passwordSaved') ? 'text-xs text-accent-400' : 'text-xs text-red-400'}>{passwordMessage}</p>}
                <button onClick={savePassword} disabled={!newPassword || !confirmPassword || passwordSaving} className="w-full rounded-lg bg-accent-500 px-3 py-2.5 text-sm font-semibold text-background-950 hover:bg-accent-500/90 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer">{passwordSaving ? t('cinema_loading') : t('cinema_savePassword')}</button>
              </div>
            </div>}
            {activeSheet === 'playback' && <div>
              <h3 className="text-lg font-bold text-foreground-50 font-heading">{t('cinema_playbackSettings')}</h3>
              <div className="mt-5 rounded-xl bg-background-800/60 px-4 py-3.5 flex items-center justify-between gap-4"><div><p className="text-sm font-medium text-foreground-200">{t('cinema_playbackAutoplay')}</p><p className="mt-1 text-xs text-foreground-500">{t('cinema_playbackAutoplayDesc')}</p></div><button onClick={() => { const next = !autoPlayNext; setAutoPlayNext(next); window.localStorage.setItem('mfantasy_auto_play_next', String(next)); }} className={`relative h-6 w-11 rounded-full transition-colors cursor-pointer ${autoPlayNext ? 'bg-accent-500' : 'bg-background-700'}`}><span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-transform ${autoPlayNext ? 'translate-x-6' : 'translate-x-1'}`} /></button></div>
            </div>}
            {activeSheet === 'points' && <div><h3 className="text-lg font-bold text-foreground-50 font-heading">{t('cinema_coinLedger')}</h3><div className="mt-4 rounded-xl border border-accent-500/20 bg-accent-500/10 px-4 py-4"><p className="text-xs text-foreground-500">{t('cinema_pointsBalance')}</p><p className="mt-1 text-2xl font-bold text-accent-400">{member.pointsBalance}</p></div>{pointLogsLoading ? <div className="py-12 text-center text-foreground-500"><i className="ri-loader-4-line animate-spin text-xl" /></div> : pointLogs.length === 0 ? <p className="py-10 text-center text-sm text-foreground-600">{t('cinema_emptyPointLogs')}</p> : <div className="mt-4 space-y-2">{pointLogs.map((log) => <div key={log.id} className="rounded-xl bg-background-800/60 px-4 py-3"><div className="flex items-center justify-between gap-3"><p className="text-sm font-medium text-foreground-200">{pointLogLabel(t, log.changeType)}</p><span className={log.changeAmount >= 0 ? 'text-sm font-semibold text-accent-400' : 'text-sm font-semibold text-red-400'}>{log.changeAmount >= 0 ? '+' : ''}{log.changeAmount}</span></div><div className="mt-1 flex items-center justify-between gap-3 text-[11px] text-foreground-500"><span className="truncate">{displayCoinText(log.remark)}</span><span className="whitespace-nowrap">{formatDateTime(log.createdAt)}</span></div><div className="mt-1 flex items-center justify-between gap-3 text-[11px] text-foreground-600"><span>{t('cinema_pointsBalance')}: {log.balanceAfter}</span>{log.bizId > 0 && <span>{t('cinema_businessReference')}: {pointLogBizLabel(t, log.bizType)} #{log.bizId}</span>}</div></div>)}</div>}</div>}
            {activeSheet === 'orders' && <div><h3 className="text-lg font-bold text-foreground-50 font-heading">{t('cinema_rechargeOrders')}</h3>{ordersLoading ? <div className="py-12 text-center text-foreground-500"><i className="ri-loader-4-line animate-spin text-xl" /></div> : orders.length === 0 ? <p className="py-10 text-center text-sm text-foreground-600">{t('cinema_emptyRechargeOrders')}</p> : <div className="mt-4 space-y-2">{orders.map((order) => <div key={order.id} className="rounded-xl bg-background-800/60 px-4 py-3"><div className="flex items-center justify-between gap-3"><p className="truncate text-sm font-medium text-foreground-200">{order.packageTitle}</p><span className={order.status === 'paid' ? 'text-xs text-accent-400' : 'text-xs text-foreground-500'}>{t(`cinema_paymentStatus_${order.status}`)}</span></div><div className="mt-2 flex items-end justify-between gap-3"><div><p className="text-base font-bold text-accent-400">+{order.points} {t('cinema_rechargeCoins')}</p><p className="mt-1 max-w-[230px] truncate font-mono text-[10px] text-foreground-600">{order.outTradeNo}</p></div><div className="text-right"><p className="text-xs text-foreground-300">{order.currency === 'USDT' ? `${(order.amount / 100).toFixed(2)} USDT` : `¥${(order.amount / 100).toFixed(2)}`}</p><p className="mt-1 text-[10px] text-foreground-600">{formatDateTime(order.paidAt || order.createdAt)}</p></div></div></div>)}</div>}</div>}
          </div>
        </div>
      )}
      {/* Recharge Modal */}
      <CinemaRechargeModal
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        loadOptions={async () => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return fetchMemberPaymentOptions(token);
        }}
        createTicket={async (packageId, payMethod) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return createMemberPaymentTicket(packageId, payMethod, token);
        }}
        createOrder={async (packageId, payMethod) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return createMemberRechargeOrder(packageId, payMethod, token);
        }}
        queryOrder={async (outTradeNo) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return queryMemberRechargeOrder(outTradeNo, token);
        }}
        onRechargeSuccess={() => {
          void refreshCurrentMember().then(setMember);
        }}
      />
    </div>
  );
}

function pointLogLabel(t: (key: string) => string, changeType: string): string {
  const keyMap: Record<string, string> = {
    register_reward: 'cinema_pointLog_registerReward',
    app_first_login_reward: 'cinema_pointLog_appFirstLoginReward',
    invite_reward: 'cinema_pointLog_inviteReward',
    daily_check_in: 'cinema_pointLog_dailyCheckIn',
    limited_recharge_task: 'cinema_pointLog_limitedRechargeTask',
    email_binding_task: 'cinema_pointLog_emailBindingTask',
    daily_unlock_task: 'cinema_pointLog_dailyUnlockTask',
    first_episode_pass_unlock: 'cinema_pointLog_firstEpisodeUnlock',
    episode_unlock: 'cinema_pointLog_episodeUnlock',
    recharge: 'cinema_pointLog_recharge',
    first_recharge_bonus: 'cinema_pointLog_firstRechargeBonus',
    daily_recharge_bonus: 'cinema_pointLog_dailyRechargeBonus',
    admin_adjust: 'cinema_pointLog_adminAdjust',
  };
  return t(keyMap[changeType] || 'cinema_pointLog_other');
}

function pointLogBizLabel(t: (key: string) => string, bizType: string): string {
  const keyMap: Record<string, string> = {
    register: 'cinema_pointBiz_register',
    register_backfill: 'cinema_pointBiz_registerBackfill',
    app_first_login: 'cinema_pointBiz_appFirstLogin',
    invite_register: 'cinema_pointBiz_inviteRegister',
    daily_check_in: 'cinema_pointBiz_dailyCheckIn',
    recharge_twice_24h: 'cinema_pointBiz_legacyLimitedRechargeTask',
    first_recharge_1h: 'cinema_pointBiz_limitedRechargeTask',
    bind_email: 'cinema_pointBiz_emailBindingTask',
    unlock_3_episodes: 'cinema_pointBiz_unlockThreeEpisodes',
    unlock_5_episodes: 'cinema_pointBiz_unlockFiveEpisodes',
    episode_unlock: 'cinema_pointBiz_episodeUnlock',
    recharge_order: 'cinema_pointBiz_rechargeOrder',
    admin_adjust: 'cinema_pointBiz_adminAdjust',
  };
  return t(keyMap[bizType] || 'cinema_pointBiz_other');
}

function displayCoinText(value?: string) {
  return (value || '-').replaceAll('积分', '金币');
}

function formatDateTime(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
